import React, { useState, useEffect } from 'react';
import { devCenter } from '../../services/api';
import { Users, Shield, Trash2, Key } from 'lucide-react';

const TeamAccessManager = () => {
    const [team, setTeam] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newEmail, setNewEmail] = useState("");
    const [newRole, setNewRole] = useState("developer");

    useEffect(() => {
        fetchTeam();
    }, []);

    const fetchTeam = async () => {
        try {
            const res = await devCenter.getAccessList();
            setTeam(res.data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        try {
            await devCenter.addAccess({
                email: newEmail,
                role: newRole,
                modules: ["dev_command_center", "deployments", "testing", "sessions", "team_access"]
            });
            setNewEmail("");
            fetchTeam();
        } catch (err) {
            alert(err.response?.data?.detail || "Failed to grant access");
        }
    };

    const handleRevoke = async (id) => {
        if (!window.confirm("Are you sure you want to revoke this user's Dev Center access?")) return;
        try {
            await devCenter.revokeAccess(id);
            fetchTeam();
        } catch (error) {
            console.error(error);
            alert("Failed to revoke access");
        }
    };

    if (loading) return <div className="text-slate-500">Loading team...</div>;

    return (
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
            <h3 className="text-lg font-medium text-white mb-6 flex items-center gap-2">
                <Users size={18} className="text-slate-400"/>
                Team Access
            </h3>

            <form onSubmit={handleAdd} className="flex gap-4 mb-8 bg-slate-900 border border-slate-700 p-4 rounded-lg">
                <div className="flex-1">
                    <label className="text-xs text-slate-400 mb-1 block">Email Address</label>
                    <input 
                        type="email" 
                        required 
                        value={newEmail} 
                        onChange={(e) => setNewEmail(e.target.value)}
                        placeholder="teammate@example.com"
                        className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white outline-none focus:border-blue-500"
                    />
                </div>
                <div className="w-48">
                    <label className="text-xs text-slate-400 mb-1 block">Role</label>
                    <select 
                        value={newRole} 
                        onChange={(e) => setNewRole(e.target.value)}
                        className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white outline-none focus:border-blue-500"
                    >
                        <option value="admin">Admin</option>
                        <option value="developer">Developer</option>
                        <option value="tester">Tester</option>
                        <option value="viewer">Viewer</option>
                    </select>
                </div>
                <div className="flex items-end">
                    <button type="submit" className="bg-blue-600 hover:bg-blue-500 text-white rounded-md px-4 py-2 font-medium text-sm transition-colors flex items-center gap-2">
                        <Key size={14} /> Grant Access
                    </button>
                </div>
            </form>

            <div className="space-y-2">
                {team.length === 0 ? (
                    <div className="text-slate-500 text-sm py-4 text-center">No team members explicitly granted access.</div>
                ) : (
                    team.map(member => (
                        <div key={member.id} className="flex justify-between items-center bg-slate-800 p-3 rounded-lg border border-slate-700">
                            <div>
                                <div className="text-white text-sm font-medium">{member.email}</div>
                                <div className="text-xs text-slate-500 mt-1 flex items-center gap-2">
                                    <Shield size={12} />
                                    <span className="capitalize">{member.role}</span>
                                    <span>• Added by {member.added_by || "System"}</span>
                                </div>
                            </div>
                            <button 
                                onClick={() => handleRevoke(member.id)}
                                className="text-red-400 hover:bg-red-950 px-3 py-1.5 rounded-md text-sm transition-colors border border-transparent hover:border-red-900"
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                    ))
                )}
            </div>
            
            <div className="mt-8 pt-4 border-t border-slate-700 text-xs text-slate-500 text-center">
                Note: Environmental admins (defined in <code>DEV_CENTER_ADMIN_EMAILS</code>) always have full access and bypass this list.
            </div>
        </div>
    );
};

export default TeamAccessManager;
